/* eslint-disable react/prop-types */
import React, { type ReactElement } from "react"
import BaseTable, { type ColumnShape } from "react-base-table"
import type { SeriesCombination } from "interfaces/dataInterfaces"
import SelectionCell from "./SelectionCell"

type Props = {
  selectedRows: number[]
  onSelectedRowsChange: React.Dispatch<React.SetStateAction<number[]>>
  rowKey: string
  columns: ColumnShape<SeriesCombination>[]
  width: number
  data?: SeriesCombination[]
  height?: number
  className?: string
  rowHeight?: number
  headerHeight?: number
}

const SelectableTable = ({
  selectedRows,
  data,
  rowKey,
  onSelectedRowsChange,
  columns,
  width,
  height,
  className,
  rowHeight,
  headerHeight,
}: Props): ReactElement => {
  const handleSelectChange = ({ selected, rowData }): void => {
    const rowKeys = [...selectedRows]
    const key = rowData[rowKey]

    if (selected) {
      if (!rowKeys.includes(key)) rowKeys.push(key)
    } else {
      const index = rowKeys.indexOf(key)
      if (index > -1) {
        rowKeys.splice(index, 1)
      }
    }

    onSelectedRowsChange(rowKeys)
  }

  const selectionColumn = {
    width: 40,
    flexShrink: 0,
    resizable: false,
    cellRenderer: SelectionCell,
    key: "__selection__",
    rowKey,
    selectedRowKeys: selectedRows,
    onChange: handleSelectChange,
    headerRenderer: ({ container }) => {
      const { props } = container
      const allSelected = props.data?.length > 0 && props.data?.every(row => selectedRows.includes(row[rowKey]))
      return (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={event => {
            if (event.target.checked) {
              const keys = props.data?.map(row => row[rowKey]).filter(key => !selectedRows.includes(key))
              onSelectedRowsChange(prev => [...prev, ...keys])
            } else {
              const keys = props.data?.map(row => row[rowKey])
              onSelectedRowsChange(prev => prev.filter(key => !keys.includes(key)))
            }
          }}
        />
      )
    },
  }

  columns = [selectionColumn, ...columns]

  return (
    <BaseTable
      columns={columns}
      data={data ?? []}
      width={width}
      height={height}
      rowKey={rowKey}
      className={className}
      rowHeight={rowHeight}
      headerHeight={headerHeight}
      emptyRenderer={() => <p>No records to display</p>}
    />
  )
}

export default SelectableTable
